<template>
  <div class="collection-details-container">
    <div class="shared-info-message" v-if="isReadOnly(collection)">
      <BbIcon size="small" name="information" type="filled" />
      <TextBody
        tag="span"
        i18n-key="collections.sharedCollection.sharedCollectionInfoMessage"
        :body-type="5"
      ></TextBody>
    </div>
    <span class="collection-title">{{ collection?.collectionName || '' }}</span>
    <span class="collection-description">{{ collection?.collectionDescription || '' }}</span>
    <div class="owner" v-if="isReadOnly(collection)">
      <TextBody tag="span" i18n-key="collections.sharedCollection.ownedBy" :body-type="1"></TextBody>
      <span class="collection-title">{{ ownerData }}</span>
    </div>
    <div class="collection-details">
      <span>{{ documentsCount }}</span>
      <span class="bullet">•</span>
      <span>{{ formatUpdatedAt }}</span>
    </div>
    <div class="collection-actions">
      <BbBtn v-if="!isReadOnly(collection)" size="small" type="secondary" @click="editCollection">
        <BbIcon size="small" name="edit" />
        <TextBody i18n-key="common.edit"></TextBody>
      </BbBtn>
      <BbBtn v-if="!isReadOnly(collection)" size="small" type="secondary" @click="shareCollection">
        <BbIcon size="small" name="share" />
        <TextBody i18n-key="common.share"></TextBody>
      </BbBtn>
      <BbBtn size="small" type="secondary" @click="openDeletePopup">
        <BbIcon size="small" name="delete" />
        <TextBody i18n-key="common.delete"></TextBody>
      </BbBtn>
    </div>
    <div class="documents-list" v-if="!isDataLoading">
      <FavoriteDocumentCard
        v-for="(reference, index) in collectionDocuments"
        :key="reference.externalId || reference"
        :collection-id="collectionId"
        :is-read-only="isReadOnly(collection)"
        :reference="reference"
        :index="index"
      />
    </div>
    <FavoritesSkeletonLoader v-else />
    <WarningPopup
      :popup-opened="confirmDeletePopupOpened"
      title-key="collections.deleteCollection"
      warning-message-key="collections.collectionsWarning"
      confirm-text-key="modals.confirmDeleteText"
      @update:open="confirmDeletePopupOpened = false"
      @execute:action="confirmDelete"
    />
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n/dist/vue-i18n';
import TextBody from '~/components/common/TextBody';
import useCollections from '~/composables/useCollections';
import FavoriteDocumentCard from '~/components/conversation/favoritesPanel/FavoriteDocumentCard';
import FavoritesSkeletonLoader from '~/components/conversation/favoritesPanel/FavoritesSkeletonLoader';
import { calculatePassedTime } from '~/utils/date-utils';
import useModals from '~/composables/useModals';
import WarningPopup from '~/components/common/WarningPopup';
import useAuthData from '@/composables/useAuthData';

export default {
  components: { TextBody, FavoriteDocumentCard, FavoritesSkeletonLoader, WarningPopup },
  props: {
    collectionId: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const { setAdditionalDocumentsData, getCollection, removeCollection, isReadOnly } = useCollections();
    const { setCreateCollectionPopup, setSharedLinkCollectionPopup } = useModals();
    const { getUserData } = useAuthData();

    const isDataLoading = ref(false);
    const confirmDeletePopupOpened = ref(false);
    const ownerData = ref('');

    const collection = computed(() => getCollection(props.collectionId).value);
    const collectionDocuments = computed(() => collection.value?.documents || []);
    const formatUpdatedAt = computed(() => calculatePassedTime(collection.value?.modifiedAt || ''));
    const documentsCount = computed(
      () => `${(collection.value && collection.value.documents?.length) || 0}  ${t('collections.documents')}`,
    );

    const editCollection = () => setCreateCollectionPopup({ open: true, collection: collection.value });
    const shareCollection = () => setSharedLinkCollectionPopup({ open: true, collectionId: props.collectionId });
    const openDeletePopup = () => {
      if (collection.value.documents.length) {
        confirmDeletePopupOpened.value = true;
      } else {
        removeCollection(props.collectionId);
        emit('navigate');
      }
    };
    const confirmDelete = () => {
      removeCollection(props.collectionId);
      confirmDeletePopupOpened.value = false;
      emit('navigate');
    };

    // need this in order to reload data when user chooses another collection
    watch(
      () => props.collectionId,
      async (newId, oldId) => {
        if (newId && newId !== oldId) {
          ownerData.value = '';
          isDataLoading.value = true;
          await setAdditionalDocumentsData(newId);
          isDataLoading.value = false;
          if (isReadOnly(collection.value)) {
            const { name = '', nickname = '', email = '' } = (await getUserData(collection.value.owner)) || {};
            ownerData.value = name || nickname || email;
          }
        }
      },
      { immediate: true },
    );

    return {
      confirmDelete,
      confirmDeletePopupOpened,
      collection,
      collectionDocuments,
      documentsCount,
      editCollection,
      formatUpdatedAt,
      isDataLoading,
      isReadOnly,
      openDeletePopup,
      ownerData,
      removeCollection,
      shareCollection,
    };
  },
};
</script>

<style scoped lang="scss">
.collection-details-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  .shared-info-message {
    display: flex;
    gap: $bb-spacing-2;
    background-color: $bb-neutral-0;
    padding: $bb-spacing-2;
    border-radius: $bb-spacing-5;
    margin-bottom: $bb-spacing-2;
    margin-top: $bb-spacing-2;

    :deep(span) {
      color: black !important;
    }

    i {
      color: $bb-interface-13;
    }
  }

  .collection-title {
    @include bb-regular-text-styles('md');
    font-weight: 500;
    margin-top: $bb-spacing-3;
  }

  .collection-description {
    @include bb-regular-text-styles('md');
  }

  .owner {
    margin-top: $bb-spacing-3;
  }

  .collection-details {
    @include bb-regular-text-styles('xs');
    margin-top: $bb-spacing-3;

    span {
      color: $bb-neutral-3;
      letter-spacing: 0.1px;
      font-size: 13px;
    }

    .bullet {
      margin: 0 $bb-spacing-1;
    }
  }

  .collection-actions {
    display: flex;
    gap: $bb-spacing-1;
    margin-top: $bb-spacing-2;

    button {
      flex-grow: 1;
    }
  }

  .documents-list {
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-4;
    margin-top: $bb-spacing-5;
  }
}
</style>
